// src/App.tsx

import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import { announcementApi, GenerationResult, AnnouncementFormData } from './api/announcementApi';
import { config } from './config';
import { Client } from '@microsoft/microsoft-graph-client';
import { Message } from '@microsoft/microsoft-graph-types';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import './App.css';

const TONES = ['Fun', 'Serious', 'Inspirational'] as const;
type Tone = typeof TONES[number];

const COMMUNICATION_GOALS = [
  'Share information (non-essential)',
  'Increase awareness',
  'Improve understanding',
  'Encourage action (optional)',
  'Request action (mandatory)',
  'Influence behaviour change',
  'Involve and engage',
  'Recognise or celebrate',
  'Promote our culture'
];

const BUSINESS_AREAS = [
  'Bidcorp UK',
  'Bidfood',
  'Bidfresh',
  'CSD',
  'CFBG',
  'Manufacturing'
];

const CHANNELS = [
  'Announcements',
  'People First newsfeed app',
  'Promotional poster',
  'SharePoint news articles',
  'CFBG newspaper'
];

// Email domain to business area mapping (editable)
const EMAIL_TO_BUSINESS_AREA: Record<string, string> = {
  'bidfood': 'Bidfood',
  'bidcorp': 'Bidcorp UK',
  'bidfresh': 'Bidfresh',
  'csd': 'CSD',
  'cfbg': 'CFBG',
  'manufacturing': 'Manufacturing'
};

// Audience options by business area
const AUDIENCE_BY_BUSINESS_AREA: Record<string, string[]> = {
  'Bidfood': [
    'Bidfood',
    'Business Support',
    'Wholesale All',
    'Transport',
    'Warehouse',
    'Sales Centre',
    'Field Sales',
    'Wholesale central',
    'Wholesale (HOO/HOS)',
    'Bidfood senior leaders',
    'Bidfood people managers',
    'Business Support senior leaders',
    'Business Support people managers'
  ],
  'CFBG': [
    'CFBG All',
    'CFBG Central',
    'Elite',
    'Harvest',
    'Thomas Ridley',
    'Caterfood',
    'South Lincs',
    'Nicol Hughes',
    'CFBG senior leaders',
    'CFBG people managers'
  ],
  'Bidfresh': [
    'Bidfresh All',
    'Bidfresh Central',
    'Kingfisher Brixham',
    'Daily Fish',
    'Direct Seafoods Colchester',
    'Direct Seafoods London',
    'Kingfisher Midlands',
    'Neve\'s Fleetwood',
    'R Noone & Son',
    'Taylor Foods',
    'Bidfresh senior leaders',
    'Bidfresh people managers'
  ],
  'Manufacturing': [
    'Manufacturing All',
    'Yarde Farm',
    'Simply Food Solutions',
    'Northern Bloc',
    'Manufacturing senior leaders',
    'Manufacturing people managers'
  ],
  'Bidcorp UK': [
    'All group senior leaders',
    'All group people managers',
    'All group employees'
  ]
};

// Channel options by business area
const CHANNELS_BY_BUSINESS_AREA: Record<string, string[]> = {
  'Bidfood': [
    'Announcements',
    'People First newsfeed app',
    'Promotional poster',
    'SharePoint news articles'
  ],
  'Bidfresh': [
    'Announcements',
    'People First newsfeed app',
    'Promotional poster'
  ],
  'CFBG': [
    'People First newsfeed app',
    'Promotional poster',
    'CFBG newspaper'
  ],
  'Manufacturing': [
    'People First newsfeed app',
    'Promotional poster'
  ],
  // For undefined business areas, show all channels
  'Bidcorp UK': [
    'Announcements',
    'People First newsfeed app',
    'Promotional poster'
  ],
  'CSD': [
    'Announcements',
    'People First newsfeed app',
    'Promotional poster'
  ]
};

// Helper function to detect business area from email
function detectBusinessAreaFromEmail(email: string): string {
  if (!email) return '';
  
  const emailLower = email.toLowerCase();
  
  for (const [keyword, businessArea] of Object.entries(EMAIL_TO_BUSINESS_AREA)) {
    if (emailLower.includes(keyword)) {
      return businessArea;
    }
  }
  
  return '';
}

// Helper function to convert markdown to HTML with basic formatting
function markdownToHtml(markdown: string): string {
  let html = markdown;
  
  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  
  // Line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  
  // Wrap in paragraph if not already in tags
  if (!html.startsWith('<')) {
    html = '<p>' + html + '</p>';
  }
  
  return html;
}

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Metadata fields (not sent to API)
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [team, setTeam] = useState('');
  const [businessArea, setBusinessArea] = useState('');
  const [channel, setChannel] = useState('');
  const [audience, setAudience] = useState<string[]>([]);
  const [keyContact, setKeyContact] = useState('');
  const [sender, setSender] = useState('');
  const dateOfRequest = new Date().toISOString().split('T')[0]; // YYYY-MM-DD, sent with the email but not shown on the form
  const [deadline, setDeadline] = useState('');
  const [approver, setApprover] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [attachmentSizeError, setAttachmentSizeError] = useState<string | null>(null);

  const [formData, setFormData] = useState<AnnouncementFormData>({
    communicationGoal: '',
    mainTitle: '',
    keyMessage: '',
  });
  const [commentsForComms, setCommentsForComms] = useState('');

  // Channel-specific fields
  const [newsfeedTiming, setNewsfeedTiming] = useState('');
  const [newsfeedApprovals, setNewsfeedApprovals] = useState('');
  const [posterDisplayTiming, setPosterDisplayTiming] = useState('');
  const [posterPrintingType, setPosterPrintingType] = useState('');
  const [tabloidApprover, setTabloidApprover] = useState('');
  
  const [selectedTone, setSelectedTone] = useState<Tone>('Serious');
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'draft' | 'pending' | 'approved' | null>(null);
  
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [hasBeenEdited, setHasBeenEdited] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
  const [showGenerateWarning, setShowGenerateWarning] = useState(false);

  // Fetch user profile from Microsoft Graph to get jobTitle and department
  const fetchUserProfile = async () => {
    if (accounts.length === 0) return;

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=displayName,mail,jobTitle,department', {
        headers: {
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
      });

      if (response.ok) {
        const profile = await response.json();
        setRole(profile.jobTitle || '');
        setTeam(profile.department || '');
      }
    } catch (error) {
      console.error('Failed to fetch user profile from Graph API:', error);
      // Silently fail - role and team will remain empty
    }
  };

  // Populate name and email from logged-in user
  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      const userName = account.name || account.username || '';
      const userEmail = account.username || '';
      
      setName(userName);
      setEmail(userEmail);
      
      // Auto-detect and set business area based on email
      const detectedBusinessArea = detectBusinessAreaFromEmail(userEmail);
      if (detectedBusinessArea) {
        setBusinessArea(detectedBusinessArea);
      }
      
      // Fetch additional profile data from Graph API
      fetchUserProfile();
    }
  }, [accounts]);

  // Clear audience whenever business area changes
  useEffect(() => {
    setAudience([]);
  }, [businessArea]);

  // Clear channel when business area changes if the selected channel is not available for the new business area
  useEffect(() => {
    if (businessArea && channel) {
      const availableChannels = CHANNELS_BY_BUSINESS_AREA[businessArea] || CHANNELS;
      if (!availableChannels.includes(channel)) {
        setChannel('');
      }
    }
  }, [businessArea]);

  // Validate attachment sizes (4MB limit)
  const validateAttachments = (files: File[]): boolean => {
    const MAX_SIZE = 4 * 1024 * 1024; // 4MB
    for (const file of files) {
      if (file.size > MAX_SIZE) {
        setAttachmentSizeError(`File "${file.name}" exceeds 4MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
        return false;
      }
    }
    setAttachmentSizeError(null);
    return true;
  };

  // Reset GDPR consent when attachments are removed
  useEffect(() => {
    if (attachments.length === 0) {
      setGdprConsent(false);
    }
  }, [attachments]);

  const handleLogin = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getUserName = () => {
    if (accounts.length > 0) {
      return accounts[0].name || accounts[0].username;
    }
    return 'User';
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
              📢 Internal Communications Creator
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Sign in with your Microsoft account to continue
            </p>
          </div>
          <div className="mt-8">
            <button
              onClick={handleLogin}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent 
                       text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Sign in with Microsoft
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (field: keyof AnnouncementFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return formData.communicationGoal.trim() && formData.mainTitle.trim() && formData.keyMessage.trim() && channel.trim();
  };

  const handleGenerate = async () => {
    // Check if text has been edited and show warning if so
    if (hasBeenEdited) {
      setShowGenerateWarning(true);
      return;
    }
    
    await performGenerate();
  };

  const performGenerate = async () => {
    setIsLoading(true);
    setError(null);
    setStatus('draft');
    setIsEditing(false);
    setHasBeenEdited(false);
    setShowGenerateWarning(false);
    
    try {
      // Extract user information from MSAL account
      const userInfo = accounts.length > 0 ? {
        userId: accounts[0].localAccountId || accounts[0].homeAccountId,
        userName: accounts[0].name || accounts[0].username,
        userEmail: accounts[0].username, // username typically contains the email
      } : {
        userId: '',
        userName: '',
        userEmail: '',
      };

      const data = await announcementApi.generate({
        announcement: {
          ...formData,
          keyContact,
          ...(channel === 'People First newsfeed app' && {
            newsfeedTiming,
            newsfeedApprovals,
          }),
          ...(channel === 'Promotional poster' && {
            posterDisplayTiming,
            posterPrintingType,
          }),
          ...(channel === 'CFBG newspaper' && {
            tabloidApprover,
          }),
        },
        tone: selectedTone,
        channel: channel,
        user: userInfo,
      });
      setResult(data);
      setEditableContent(data.content);
    } catch (err) {
      setError(err instanceof Error ? err.message :  'Generation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = () => {
    setShowRegenerateWarning(true);
  };

  const performRegenerate = async () => {
    setShowRegenerateWarning(false);
    setIsEditing(false);
    setHasBeenEdited(false);
    await performGenerate();
  };

  const handleEditClick = () => {
    setIsEditing(true);
    if (result) {
      setEditableContent(result.content);
    }
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
    setHasBeenEdited(true);
    if (result) {
      setResult({
        ...result,
        content: editableContent
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (result) {
      setEditableContent(result.content);
    }
  };

  const handleFinalChecks = async () => {
    if (!result) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const contentToCheck = hasBeenEdited ? editableContent : result.content;
      const data = await announcementApi.finalChecks(contentToCheck);
      setResult(data);
      setEditableContent(data.content);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Final checks failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!result) return;
    
    setStatus('pending');
    
    try {
      await announcementApi.submitForApproval({
        originalText: JSON.stringify(formData),
        generatedText: result.content,
        tone: selectedTone,
      });
    } catch (err) {
      setError(err instanceof Error ?  err.message : 'Submission failed');
      setStatus('draft');
    }
  };

  const handleExport = async (format: 'word' | 'powerpoint') => {
    if (!result) return;
    
    try {
      const blob = format === 'word' 
        ? await announcementApi.exportToWord(result.content, 'Announcement')
        : await announcementApi.exportToPowerPoint(result.content, 'Announcement');
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `announcement.${format === 'word' ?  'docx' : 'pptx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ?  err.message : 'Export failed');
    }
  };

  const handleSubmitToInternalComms = async () => {
    // Validate attachments first
    if (!validateAttachments(attachments)) {
      return;
    }
    
    // Check GDPR consent if attachments exist
    if (attachments.length > 0 && !gdprConsent) {
      setError('Please confirm you have consent to use all attachments, in line with GDPR requirements.');
      return;
    }
    
    // Show confirmation warning
    setShowSubmitWarning(true);
  };

  const performSubmitToInternalComms = async () => {
    setShowSubmitWarning(false);
    
    if (!result) {
      setError('No announcement generated yet');
      return;
    }

    // Validation
    if (!formData.communicationGoal || !formData.mainTitle || !formData.keyMessage) {
      setError('Please fill in all required fields (Communication Goal, Main Title, and Key Message)');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert attachments to base64
      const attachmentData: any[] = [];
      for (const file of attachments) {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64Content = result.split(',')[1];
            resolve(base64Content);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        attachmentData.push({
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBytes: base64,
        });
      }

      // Prepare request data
      const requestData = {
        businessArea,
        channel,
        generatedContent: editableContent, // Use the current edited content
        sourcesUsed: result.sourcesUsed || [],
        requestorInfo: {
          name,
          email,
          role,
          team,
          businessArea,
          channel,
          audience: audience.join(', '), // Convert array to comma-separated string
          keyContact,
          sender,
          tone: selectedTone,
          dateOfRequest,
          deadline,
          approver,
        },
        announcementDetails: {
          ...formData,
          commentsForComms, // Include comments for comms in the details sent to email
          // Channel-specific fields
          ...(channel === 'People First newsfeed app' && {
            newsfeedTiming,
            newsfeedApprovals,
          }),
          ...(channel === 'Promotional poster' && {
            posterDisplayTiming,
            posterPrintingType,
          }),
          ...(channel === 'CFBG newspaper' && {
            tabloidApprover,
          }),
        },
        attachments: attachmentData,
        recipientEmail: config.internalCommsEmail,
      };

      // Call Azure Function
      const response = await fetch(`${config.apiBaseUrl}/send-announcement-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to send email');
      }

      const responseData = await response.json();
      
      setStatus('pending');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error submitting to Internal Communications:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit to Internal Communications');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <header className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                📢 Internal Communications Creator
              </h1>
              <p className="text-gray-600 mt-2">
                AI-powered Internal Communications assistant - complete the form, review the draft and make any changes you need.
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Signed in as</p>
              <p className="text-sm font-medium text-gray-900">
                {getUserName()}
              </p>
              <button
                onClick={handleLogout}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Sign out
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Input Section */}
          <div className="space-y-4">
            {/* Metadata Section */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Requestor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requestor name
                  </label>
                  <input
                    type="text"
                    value={name}
                    readOnly
                    className="w-full p-3 border-2 border-gray-300 rounded-lg bg-gray-50
                               transition-colors"
                    placeholder="Your name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors"
                    placeholder="your.email@company.com"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors"
                    placeholder="Your role"
                  />
                </div>

                {/* Team */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team
                  </label>
                  <input
                    type="text"
                    value={team}
                    onChange={(e) => setTeam(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors"
                    placeholder="Your team"
                  />
                </div>

                {/* Business Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Intended business area
                  </label>
                  <select
                    value={businessArea}
                    onChange={(e) => setBusinessArea(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors bg-white"
                  >
                    <option value="">Select a business area...</option>
                    {BUSINESS_AREAS.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>

                

                {/* Audience for Comms */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Audience for comms (select all that apply)
                  </label>
                  {!AUDIENCE_BY_BUSINESS_AREA[businessArea] ? (
                    <div className="w-full p-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-500">
                      Select a business area first
                    </div>
                  ) : (
                    <div>
                      {/* Selected audiences as tags */}
                      {audience.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {audience.map((aud) => (
                            <span
                              key={aud}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                            >
                              {aud}
                              <button
                                onClick={() => setAudience(audience.filter(a => a !== aud))}
                                className="hover:text-blue-900 font-bold"
                                type="button"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Checkbox list */}
                      <div className="border-2 border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-white">
                        {AUDIENCE_BY_BUSINESS_AREA[businessArea]?.map((aud) => (
                          <label
                            key={aud}
                            className="flex items-center gap-2 py-1.5 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={audience.includes(aud)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAudience([...audience, aud]);
                                } else {
                                  setAudience(audience.filter(a => a !== aud));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 
                                         border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{aud}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Date of Publication */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of publication
                  </label>
                  <input
                    type="date"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors"
                  />
                </div>

                {/* File Attachments */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Attachments (e.g. supporting images, diagrams, or documents)
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Maximum file size is 4MB per file. If you need to share larger files, please send them directly to{' '}
                    <a href="mailto:communications@bidfood.co.uk" className="text-blue-600 hover:text-blue-800 underline">
                      communications@bidfood.co.uk
                    </a>.
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        const files = Array.from(e.target.files);
                        if (validateAttachments(files)) {
                          setAttachments([...attachments, ...files]);
                        }
                      }
                    }}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors file:mr-4 file:py-2 file:px-4
                               file:rounded-lg file:border-0 file:text-sm file:font-semibold
                               file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {attachmentSizeError && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      ⚠️ {attachmentSizeError}
                    </div>
                  )}
                  {attachments.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Selected files:</p>
                      <ul className="mt-1 space-y-1">
                        {attachments.map((file, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-center gap-2">
                            <span>📎</span>
                            <span>{file.name}</span>
                            <span className="text-gray-500">({(file.size / 1024).toFixed(2)} KB)</span>
                            <button
                              onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                              className="ml-2 text-red-500 hover:text-red-700 text-xs"
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="gdprConsent"
                          checked={gdprConsent}
                          onChange={(e) => setGdprConsent(e.target.checked)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 
                                     border-gray-300 rounded"
                        />
                        <label htmlFor="gdprConsent" className="text-sm text-gray-700 cursor-pointer">
                          I confirm I have consent to use all attachments, in line with GDPR requirements
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Channel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Channel
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  {!businessArea ? (
                    <div className="w-full p-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-500">
                      Select a business area first
                    </div>
                  ) : (
                    <select
                      value={channel}
                      onChange={(e) => setChannel(e.target.value)}
                      required
                      className="w-full p-3 border-2 border-gray-300 rounded-lg 
                                 focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                 transition-colors bg-white"
                    >
                      <option value="">Select a channel...</option>
                      {(CHANNELS_BY_BUSINESS_AREA[businessArea] || CHANNELS).map((ch) => (
                        <option key={ch} value={ch}>
                          {ch}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Announcement Content Section */}
            <div className="pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Content</h3>
            </div>

            {/* Communication Goal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                What's the goal of this communication?
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                value={formData.communicationGoal}
                onChange={(e) => handleInputChange('communicationGoal', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           transition-colors bg-white"
              >
                <option value="">Select a goal...</option>
                {COMMUNICATION_GOALS.map((goal) => (
                  <option key={goal} value={goal}>
                    {goal}
                  </option>
                ))}
              </select>
            </div>

            {/* Main Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main title header - keep it short and impactful (ideally 3-8 words)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.mainTitle}
                onChange={(e) => handleInputChange('mainTitle', e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           transition-colors"
                placeholder="Approx 3-8 words"
                maxLength={100}
              />
            </div>

            {/* Key Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the key message/s you want to share. What do people need to know and why does it matter? (Think of the what, why, how, when, where and who)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={formData.keyMessage}
                onChange={(e) => handleInputChange('keyMessage', e.target.value)}
                className="w-full h-32 p-3 border-2 border-gray-300 rounded-lg 
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           transition-colors resize-y"
                placeholder="Enter key message..."
              />
            </div>

            {/* Key Contact for More Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who should be listed as the contact for more information?
              </label>
              <input
                type="text"
                value={keyContact}
                onChange={(e) => setKeyContact(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           transition-colors"
                placeholder="Contact name"
              />
            </div>

            {/* Sender/Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who should be listed as the sender or owner of this message? (name and job title)
              </label>
              <input
                type="text"
                value={sender}
                onChange={(e) => setSender(e.target.value)}
                className="w-full p-3 border-2 border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                           transition-colors"
                placeholder="Enter sender..."
              />
            </div>

            {/* Channel-Specific Fields */}
            {channel === 'People First newsfeed app' && (
              <>
                <div className="pt-4 border-t border-gray-300">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">📱 People First newsfeed app Specific Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timing (When should this be posted? Any embargo dates, deadlines, or campaign windows?)
                  </label>
                  <textarea
                    value={newsfeedTiming}
                    onChange={(e) => setNewsfeedTiming(e.target.value)}
                    className="w-full h-24 p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors resize-y"
                    placeholder="Provide timing details..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approvals (Who needs to review/approve before publishing? Provide names or roles.)
                  </label>
                  <textarea
                    value={newsfeedApprovals}
                    onChange={(e) => setNewsfeedApprovals(e.target.value)}
                    className="w-full h-24 p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors resize-y"
                    placeholder="Provide approver names or roles..."
                  />
                </div>
              </>
            )}

            {channel === 'Promotional poster' && (
              <>
                <div className="pt-4 border-t border-gray-300">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">🖼️ Promotional Poster Specific Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    When should this be displayed? (Include start and end dates or key timings)
                  </label>
                  <textarea
                    value={posterDisplayTiming}
                    onChange={(e) => setPosterDisplayTiming(e.target.value)}
                    className="w-full h-24 p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors resize-y"
                    placeholder="Provide display timing..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Is the poster to be professionally printed, or is it for local A4?
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="posterPrintingType"
                        value="Professionally printed"
                        checked={posterPrintingType === 'Professionally printed'}
                        onChange={(e) => setPosterPrintingType(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Professionally printed</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="posterPrintingType"
                        value="Local A4"
                        checked={posterPrintingType === 'Local A4'}
                        onChange={(e) => setPosterPrintingType(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Local A4</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            {channel === 'CFBG newspaper' && (
              <>
                <div className="pt-4 border-t border-gray-300">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">📰 CFBG newspaper Specific Information</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Final version content approver name
                  </label>
                  <input
                    type="text"
                    value={tabloidApprover}
                    onChange={(e) => setTabloidApprover(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg 
                               focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                               transition-colors"
                    placeholder="Enter approver name..."
                  />
                </div>
              </>
            )}
          </div>

          {/* Tone Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Tone
            </label>
            <div className="flex gap-3">
              {TONES.map((tone) => (
                <button
                  key={tone}
                  onClick={() => setSelectedTone(tone)}
                  className={`px-5 py-2.5 rounded-lg font-medium transition-colors ${
                    selectedTone === tone
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tone === 'Fun' && '🎉 '}
                  {tone === 'Serious' && '📋 '}
                  {tone === 'Inspirational' && '✨ '}
                  {tone}
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <span className="text-red-700 flex-1">{error}</span>
                <button 
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700 ml-4 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={isLoading || isSubmitting || !isFormValid()}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-medium
                       hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
          >
            {isLoading ?  (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" 
                          stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating...
              </span>
            ) : (
              result ? '🔄 Regenerate Communication' : '✨ Generate Communication'
            )}
          </button>

          {/* Output Section */}
          {result && (
            <div className="border-t pt-6">
              {/* Instructional text */}
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Please review your generated announcement below. If you'd like to make changes, click <strong>Edit Generated Text</strong> and update the content in the edit section. When you're happy with your edits, click <strong>Save Changes</strong>.
                  <br />
                  Use the <strong>Final Checks</strong> button to run a grammar and spelling review.
                  <br />
                  Alternatively, click <strong>Regenerate Communication</strong> for a fresh version.
                  <br />
                  Once you're satisfied, press <strong>Send to Internal Communications</strong> to complete the process.
                </p>
              </div>
              
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Generated Communication 
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 
                                   rounded text-xs">
                    {selectedTone} Tone
                  </span>
                </label>
                {/* {status && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    status === 'draft' ? 'bg-gray-100 text-gray-600' :
                    status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span> 
                )}*/}
              </div>
              
              {!isEditing ? (
                <div className="p-6 bg-white rounded-lg border border-gray-200 prose prose-blue max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({node, ...props}) => <h1 className="text-2xl font-bold mb-4 text-gray-900" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-xl font-bold mb-3 text-gray-900" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-lg font-bold mb-2 text-gray-900" {...props} />,
                      p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                      em: ({node, ...props}) => <em className="italic" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
                      li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                      a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-800 underline font-medium" target="_blank" rel="noopener noreferrer" {...props} />,
                    }}
                  >
                    {result.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="bg-white rounded-lg border-2 border-blue-500">
                  <div className="p-4 bg-blue-50 border-b border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Editing Mode:</strong> Use the toolbar buttons to format your text.
                    </p>
                  </div>
                  <div className="p-4">
                    <MDEditor
                      value={editableContent}
                      onChange={(value) => setEditableContent(value || '')}
                      preview="live"
                      height={400}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="p-4 border-t border-gray-200 flex gap-3">
                    <button
                      onClick={handleSaveEdit}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      💾 Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      ✕ Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Sources Used - HIDDEN from requestor but available in backend */}
              {/* {result.sourcesUsed && result.sourcesUsed.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm font-medium text-gray-700">Sources used:</span>
                  <ul className="mt-2 space-y-1">
                    {result.sourcesUsed.map((source, index) => (
                      <li key={index} className="text-sm text-gray-600">
                        {source.url ? (
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {source.title}
                          </a>
                        ) : (
                          <span>{source.title}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Comments for Internal Communications Team - only shown after generation */}
              <div className="mt-6 p-4 bg-gray-50 border-2 border-gray-300 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Are there any other comments for the Internal Communications team?
                </label>
                <textarea
                  value={commentsForComms}
                  onChange={(e) => setCommentsForComms(e.target.value)}
                  className="w-full h-24 p-3 border-2 border-gray-300 rounded-lg 
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                             transition-colors resize-y bg-white"
                  placeholder="Add any additional comments or context for the Internal Communications team..."
                />
              </div>

              {/* Action Buttons */}
              {!isEditing && (
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={handleEditClick}
                    disabled={isLoading || isSubmitting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg 
                               hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✏️ Edit generated text
                  </button>
                  <button
                    onClick={handleFinalChecks}
                    disabled={isLoading || isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg 
                               hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ✓ Final checks
                  </button>
                  <button
                    onClick={handleSubmitToInternalComms}
                    disabled={isLoading || isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg 
                               hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" 
                                  stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      '✅ Submit to Internal Communications'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Warning Modals */}
        {showRegenerateWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                ⚠️ Confirm Regenerate
              </h3>
              <p className="text-gray-700 mb-4">
                Are you sure you want to regenerate the announcement? This will discard any edits you've made.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRegenerateWarning(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={performRegenerate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Yes, Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {showGenerateWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                ⚠️ Unsaved Edits
              </h3>
              <p className="text-gray-700 mb-4">
                You have edited the generated text. Generating a new announcement will discard your edits. Continue?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowGenerateWarning(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={performGenerate}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Yes, Generate New
                </button>
              </div>
            </div>
          </div>
        )}

        {showSubmitWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                ⚠️ Confirm submission
              </h3>
              <p className="text-gray-700 mb-4">
                Once this announcement has been submitted to Internal Communications, I'm unable to make any further edits for you here, but you can contact <a href="mailto:communications@bidfood.co.uk" className="text-blue-600 hover:text-blue-800 underline">communications@bidfood.co.uk</a> to request any tweaks. Are you sure you want to proceed?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowSubmitWarning(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={performSubmitToInternalComms}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Yes, submit
                </button>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
              {/* <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div> */}
              <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">
                ✅ Success!
              </h3>
              <p className="text-gray-700 mb-6 text-center">
                Your announcement has been submitted successfully to Internal Communications! If you have any questions or need to make changes, please contact <a href="mailto:communications@bidfood.co.uk" className="text-blue-600 hover:text-blue-800 underline">communications@bidfood.co.uk</a>.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Start a fresh communication
                </button>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Keep my details
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;